// Scoped adaptations of beui.dev/r/{tabs,button-stateful,animated-badge}.
// Retain the upstream MIT notice in LICENSE.txt.
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, Circle, LoaderCircle, Save } from "lucide-react";
import type { ReactNode } from "react";

/** Controlled tabs for the Admin policy panel. The owner can refuse departure
 * to preserve unsaved text; cancelled keyboard selection keeps the active tab. */
export function PolicyTabs({ id, options, value, disabled, onChange, label = "Policy document" }: {
  id: string; options: { value: string; label: string }[]; value: string; disabled: boolean;
  onChange: (value: string) => boolean; label?: string;
}) {
  const reduced = useReducedMotion();
  return <div className="policy-tabs" role="tablist" aria-label={label}>
    {options.map((option, index) => <button key={option.value} type="button" id={`${id}-${option.value}`} role="tab"
      aria-selected={value === option.value} aria-controls={`${id}-panel`} tabIndex={value === option.value ? 0 : -1}
      disabled={disabled} onClick={() => onChange(option.value)} onKeyDown={event => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault(); event.stopPropagation();
        const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1
          : (index + (event.key === "ArrowRight" ? 1 : -1) + options.length) % options.length;
        if (onChange(options[next].value)) event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("button")[next]?.focus();
      }}>
      {value === option.value && <motion.span className="policy-tab-indicator" layoutId={id}
        transition={reduced ? { duration: 0 } : { type: "spring", stiffness: 360, damping: 32 }} />}
      <span>{option.label}</span>
    </button>)}
  </div>;
}

export function PolicyBadge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "green" | "amber" }) {
  const reduced = useReducedMotion();
  return <motion.span className={`policy-badge policy-badge-${tone}`} initial={reduced ? false : { opacity: 0, scale: .96 }} animate={{ opacity: 1, scale: 1 }}>
    {tone === "green" ? <Check size={13} aria-hidden="true" /> : <Circle size={7} fill="currentColor" aria-hidden="true" />}{children}
  </motion.span>;
}

export function PolicySaveButton({ state, disabled, onClick }: {
  state: "idle" | "saving" | "saved"; disabled: boolean; onClick: () => void;
}) {
  const reduced = useReducedMotion();
  return <motion.button type="button" className="policy-button policy-button-dark" disabled={disabled} onClick={onClick}
    whileTap={disabled || reduced ? undefined : { scale: .97 }}>
    <AnimatePresence mode="wait" initial={false}><motion.span key={state} className="policy-button-label"
      initial={reduced ? false : { opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
      exit={reduced ? undefined : { opacity: 0, y: -5 }} transition={{ duration: .14 }}>
      {state === "saving" ? <LoaderCircle size={16} className="motion-safe:animate-spin" aria-hidden="true" /> : state === "saved" ? <Check size={16} aria-hidden="true" /> : <Save size={16} aria-hidden="true" />}
      {state === "saving" ? "Saving…" : state === "saved" ? "Draft saved" : "Save draft"}
    </motion.span></AnimatePresence>
  </motion.button>;
}
